import { useEffect, useState, useCallback } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Broadcast, Crosshair, Trash, Link as LinkIcon, ShieldCheck, FileCode } from "@phosphor-icons/react";
import { toast } from "sonner";

const TIER_COLOR = ["#71717A", "#00F0FF", "#00F0FF", "#FFCC00", "#FF9500", "#FF3B30"];
const sevColor = { critical: "#FF3B30", high: "#FF9500", medium: "#FFCC00", low: "#00F0FF", info: "#71717A" };

export default function Intercept() {
  const [target, setTarget] = useState("");
  const [description, setDescription] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [report, setReport] = useState(null);
  const [hunts, setHunts] = useState([]);
  const [openFinding, setOpenFinding] = useState(null);

  const loadHistory = useCallback(async () => {
    const { data } = await api.get("/mythos/hunts");
    setHunts(data.hunts);
  }, []);
  useEffect(() => { loadHistory(); }, [loadHistory]);

  const hunt = async (e) => {
    e.preventDefault();
    if (!target.trim()) return;
    setBusy(true);
    setReport(null);
    try {
      const { data } = await api.post("/mythos/hunt", { target: target.trim(), description: description.trim() || null, code: code.trim() || null });
      setReport(data.report);
      setHunts((p) => [data, ...p]);
      toast.success(`Hunt complete · ${data.report.findings?.length || 0} findings`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Hunt failed");
    } finally { setBusy(false); }
  };

  const remove = async (id) => {
    await api.delete(`/mythos/hunts/${id}`);
    setHunts((p) => p.filter((h) => h.id !== id));
    toast.success("Hunt deleted");
  };

  const chain = report?.exploit_chain;

  return (
    <div data-testid="intercept-page">
      <div className="mb-1 flex items-center gap-2">
        <Broadcast size={18} weight="duotone" color="#00F0FF" />
        <p className="text-[10px] uppercase tracking-[0.3em] text-[#00F0FF]">Mythos · Autonomous Vulnerability Hunter</p>
      </div>
      <h1 className="font-head text-3xl tracking-tighter font-black mb-1">INTERCEPT ELITE</h1>
      <p className="text-xs text-[#71717A] mb-6 max-w-2xl">Point it at a target, description, or codebase — Intercept Elite hunts for zero-day-class vulnerabilities and builds a chained exploit narrative (defensive research only).</p>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        <div className="xl:col-span-2 space-y-6">
          <form onSubmit={hunt} className="bg-[#0C0C14] border border-[#1F1F33] p-5 space-y-4">
            <div>
              <label className="block text-[10px] uppercase tracking-[0.2em] text-[#71717A] mb-2">Target</label>
              <input data-testid="mythos-target-input" value={target} onChange={(e) => setTarget(e.target.value)}
                placeholder="acme-payments-api / https://app.acme.com / project name"
                className="w-full bg-[#05050A] border border-[#1F1F33] px-3 py-2.5 text-sm outline-none focus:border-[#00F0FF] placeholder:text-[#71717A]" />
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-[0.2em] text-[#71717A] mb-2">Description (optional)</label>
              <input data-testid="mythos-description-input" value={description} onChange={(e) => setDescription(e.target.value)}
                placeholder="Node.js payment API with JWT auth and file upload"
                className="w-full bg-[#05050A] border border-[#1F1F33] px-3 py-2.5 text-sm outline-none focus:border-[#00F0FF] placeholder:text-[#71717A]" />
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-[0.2em] text-[#71717A] mb-2">Source Code (optional)</label>
              <textarea data-testid="mythos-code-input" rows={5} value={code} onChange={(e) => setCode(e.target.value)}
                placeholder="Paste code to deep-hunt for memory safety, injection, auth-bypass, race conditions…"
                className="w-full bg-[#05050A] border border-[#1F1F33] px-3 py-2.5 text-xs font-mono outline-none focus:border-[#00F0FF] placeholder:text-[#71717A] resize-none" />
            </div>
            <button data-testid="initiate-intercept-button" type="submit" disabled={busy}
              className="flex items-center gap-2 bg-[#00F0FF] text-black font-bold uppercase tracking-[0.15em] text-[11px] px-5 py-2.5 hover:bg-white active:scale-[0.98] transition-all disabled:opacity-50">
              <Crosshair size={14} weight="fill" /> {busy ? "Hunting…" : "Initiate Intercept"}
            </button>
          </form>

          {busy && (
            <div className="bg-[#0C0C14] border border-[#1F1F33] p-10 text-center">
              <Broadcast size={32} weight="duotone" color="#00F0FF" className="mx-auto pulse-dot" />
              <p className="text-xs text-[#71717A] mt-3">Autonomous hunt in progress — reasoning through attack surface (~30-60s)…</p>
            </div>
          )}

          {report && (
            <div className="bg-[#0C0C14] border border-[#1F1F33] p-5 fade-up" data-testid="mythos-report">
              <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
                <span className="font-head text-lg font-bold">{report.target}</span>
                <div className="flex gap-1">
                  {[1, 2, 3, 4, 5].map((t) => (
                    <span key={t} className="text-[10px] font-mono px-1.5 py-0.5 border" style={{ borderColor: "#1F1F33", color: report.tier_summary?.[`tier${t}`] ? TIER_COLOR[t] : "#3a3a4a" }}>
                      T{t}:{report.tier_summary?.[`tier${t}`] || 0}
                    </span>
                  ))}
                </div>
              </div>
              <p className="text-xs text-[#A1A1AA] leading-relaxed mb-4">{report.narrative}</p>

              {report.findings?.map((f) => (
                <div key={f.finding_id} className="border border-[#1F1F33] mb-2" data-testid={`mythos-finding-${f.finding_id}`}>
                  <button onClick={() => setOpenFinding(openFinding === f.finding_id ? null : f.finding_id)} className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-white/[0.02]">
                    <span className="text-[10px] font-mono font-bold px-1.5 py-0.5" style={{ color: TIER_COLOR[f.tier] || "#71717A", border: `1px solid ${TIER_COLOR[f.tier] || "#71717A"}` }}>TIER {f.tier}</span>
                    <span className="text-[10px] uppercase font-bold" style={{ color: sevColor[f.severity] }}>{f.severity}</span>
                    <span className="text-sm flex-1 min-w-0 truncate">{f.title}</span>
                    <span className="text-[10px] text-[#71717A]">{Math.round((f.confidence || 0) * 100)}%</span>
                  </button>
                  {openFinding === f.finding_id && (
                    <div className="px-4 pb-4 text-xs text-[#A1A1AA] space-y-2 fade-up">
                      <div className="flex gap-2 flex-wrap text-[10px] font-mono">
                        {f.vuln_type && <span className="text-[#FF9500] border border-[#FF9500]/40 px-1.5">{f.vuln_type}</span>}
                        {f.cwe && <span className="text-[#00F0FF] border border-[#00F0FF]/40 px-1.5">{f.cwe}</span>}
                        {f.mitre_technique && <span className="text-[#71717A] border border-[#1F1F33] px-1.5">{f.mitre_technique}</span>}
                      </div>
                      <p>{f.description}</p>
                      {f.vulnerable_snippet && <pre className="bg-black border border-[#1F1F33] p-2 text-[10px] whitespace-pre-wrap overflow-x-auto text-[#FF9500]">{f.vulnerable_snippet}</pre>}
                      {f.proof_of_concept && (
                        <div className="border-l-2 border-[#FF3B30] pl-2">
                          <div className="text-[10px] uppercase text-[#FF3B30] mb-1">Proof of Concept (conceptual)</div>
                          <p className="text-[#A1A1AA]">{f.proof_of_concept}</p>
                        </div>
                      )}
                      {f.exploitability_notes && <p className="text-[#71717A]"><span className="uppercase text-[10px]">Exploitability: </span>{f.exploitability_notes}</p>}
                    </div>
                  )}
                </div>
              ))}

              {chain?.name && (
                <div className="mt-4 border border-[#FF3B30]/30 bg-[#FF3B30]/5 p-4" data-testid="mythos-chain">
                  <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-[#FF3B30] mb-2"><LinkIcon size={14} /> Exploit Chain</div>
                  <div className="font-head font-bold text-sm mb-2">{chain.name}</div>
                  <div className="space-y-1">
                    {(chain.steps || []).map((s, i) => (
                      <div key={i} className="text-[11px] text-[#A1A1AA]"><span className="text-[#FF9500]">[{s.role}]</span> {s.how}</div>
                    ))}
                  </div>
                  {chain.final_impact && <div className="text-[11px] text-[#FF3B30] mt-2">Final impact: {chain.final_impact}</div>}
                </div>
              )}

              {report.file_rankings?.length > 0 && (
                <div className="mt-4">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-[#71717A] mb-2 flex items-center gap-1"><FileCode size={13} /> Ranked Attack Surface</div>
                  {report.file_rankings.map((fr, i) => (
                    <div key={i} className="flex items-center gap-3 text-[11px] py-1 border-b border-[#1F1F33]">
                      <span className="font-mono text-[#FFCC00]">{fr.score}</span>
                      <span className="font-mono text-[#A1A1AA]">{fr.path}</span>
                      <span className="text-[#71717A] truncate">{fr.reason}</span>
                    </div>
                  ))}
                </div>
              )}

              {report.defenses_recommended?.length > 0 && (
                <div className="mt-4 border-t border-[#1F1F33] pt-3">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-[#3ECF8E] mb-2 flex items-center gap-1"><ShieldCheck size={13} /> Recommended Defenses</div>
                  <ul className="text-[11px] text-[#A1A1AA] space-y-1">
                    {report.defenses_recommended.map((d, i) => <li key={i}>• {d}</li>)}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>

        {/* History */}
        <div className="xl:col-span-1">
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#71717A] mb-2">Hunt History</div>
          <div className="space-y-2" data-testid="mythos-history">
            {hunts.map((h) => (
              <div key={h.id} className="p-3 border border-[#1F1F33] bg-[#0C0C14] hover:border-[#33334D] transition-colors cursor-pointer" onClick={() => { setReport(h.report); window.scrollTo({ top: 0, behavior: "smooth" }); }}>
                <div className="flex items-center justify-between gap-2">
                  <span className="font-mono text-xs truncate">{h.target}</span>
                  <button onClick={(e) => { e.stopPropagation(); remove(h.id); }} className="text-[#71717A] hover:text-[#FF3B30]"><Trash size={13} /></button>
                </div>
                <div className="text-[10px] text-[#71717A] mt-1">{h.report?.findings?.length || 0} findings · {new Date(h.created_at).toLocaleDateString()}</div>
              </div>
            ))}
            {hunts.length === 0 && <div className="text-xs text-[#71717A] py-6 text-center border border-dashed border-[#1F1F33]">No hunts yet.</div>}
          </div>
        </div>
      </div>
    </div>
  );
}
